import type { DynamicToolUIPart, UIMessage } from 'ai'
import type {
  ConversationUserView,
  UserViewToolCall as ApiUserViewToolCall,
} from '../lib/api'
import {
  isConfirmTool,
  isUserAnsweredTool,
} from '@llj/ai-service-interface/dist/entities/tool-catalogue.interface'
import { groupConsecutiveTools } from './tool-group'

// args／result 存的是 JSON 字串。解不開就原樣顯示——展開的內容是給人看的，
// 硬吞掉會讓「工具回了怪東西」變成看不見。
const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text)
  } catch {
    return text
  }
}

// 續談時把一筆 tool_call 重建成與即時串流同一種 part，ToolCard 才不必分岔。
// 固定用 output-available（展開仍看得到完整原始內容），成敗改掛 toolMetadata，
// 那是伺服器判定的，不必在前端把 result 再解析一次。
const toToolPart = (eventId: string, tool: ApiUserViewToolCall): DynamicToolUIPart => ({
  type: 'dynamic-tool',
  toolName: tool.toolName,
  toolCallId: eventId,
  // outcome 是伺服器判定的四態（#1414）：被拒絕的那一筆不會再被畫成失敗
  toolMetadata: { failed: tool.failed, elapsedMs: tool.elapsedMs, outcome: tool.outcome },
  state: 'output-available',
  input: parseJson(tool.args),
  output: parseJson(tool.result),
})

// 提問卡片（#1640）。🔴 用真正的 toolCallId：之後回答時伺服器比對的是它（#1642）。
// 等待回答的那一筆還原成「參數到齊、沒有結果」，重新整理後仍可作答。
const toQuestionPart = (eventId: string, tool: ApiUserViewToolCall): DynamicToolUIPart => {
  const base = {
    type: 'dynamic-tool' as const,
    toolName: tool.toolName,
    toolCallId: tool.toolCallId || eventId,
    input: parseJson(tool.args),
  }
  if (tool.answerStatus === 'pending') return { ...base, state: 'input-available' }
  return { ...base, state: 'output-available', output: parseJson(tool.result) }
}

// 確認卡（#1693）：還原成與即時串流同一種 output，卡片狀態只看 status。
// 等待中與執行中還沒有結果，由 proposedArgs／display 組回；之後是結果加上 display
const confirmOutputOf = (tool: ApiUserViewToolCall): unknown => {
  const display = parseJson(tool.display ?? '')
  if (tool.answerStatus === 'pending') {
    return {
      status: 'awaiting_confirmation', proposedArgs: parseJson(tool.proposedArgs ?? ''), display,
    }
  }
  if (tool.answerStatus === 'executing') return { status: 'executing', display }
  const result = parseJson(tool.result)
  return typeof result === 'object' && result !== null ? { ...result, display } : result
}

const toConfirmPart = (eventId: string, tool: ApiUserViewToolCall): DynamicToolUIPart => ({
  type: 'dynamic-tool',
  toolName: tool.toolName,
  // 🔴 真正的 toolCallId：確認與取消時伺服器比對的是它
  toolCallId: tool.toolCallId || eventId,
  state: 'output-available',
  input: parseJson(tool.args),
  output: confirmOutputOf(tool),
})

// 🔴 沒有 answerStatus 的提問（同一步多出來、記成失敗的那一份）不算一組提問，照一般工具畫（#1642）
const toHistoryToolPart = (eventId: string, tool: ApiUserViewToolCall): DynamicToolUIPart => {
  if (isUserAnsweredTool(tool.toolName) && tool.answerStatus) return toQuestionPart(eventId, tool)
  if (isConfirmTool(tool.toolName) && tool.answerStatus) return toConfirmPart(eventId, tool)
  return toToolPart(eventId, tool)
}

// 事件流 → 畫面訊息。順序即伺服器的 sequence 順序，工具因此落在它實際發生的
// 位置；連續的工具事件併成同一則，摘要行的分組才與即時串流看到的一致。
// 🔴 工具之間的助理文字各自成一則（#1437）：它本來就是逐 step 落地的事件。
const toChatMessages = (view: ConversationUserView): UIMessage[] =>
  groupConsecutiveTools(view.messages, (m) => m.tool !== null).map((item) =>
    item.kind === 'tools' ?
      {
        id: item.tools[0].eventId,
        role: 'assistant' as const,
        parts: item.tools.flatMap((m) => (m.tool ? [toHistoryToolPart(m.eventId, m.tool)] : [])),
      } :
      {
        id: item.part.eventId,
        role: item.part.role,
        // 思考（#1739）還原成與即時串流同一種 reasoning part
        parts: [item.part.thought ?
          { type: 'reasoning' as const, text: item.part.thought.text, state: 'done' as const } :
          { type: 'text' as const, text: item.part.text }],
      })

export { toChatMessages }
